import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Typography } from '@/components/primitives/Typography';
import { consentsSelector } from '@/features/configuration/configurationSelector';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { sanitizeMarkup } from '@/utils/sanitizeMarkup';
import { type FC, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface RefundModalOverviewFooterProps {
  handleBackToSelection: () => void;
  handleConfirmRefund: () => void;
  loading: boolean;
}

const RefundModalOverviewFooter: FC<RefundModalOverviewFooterProps> = ({
  handleBackToSelection,
  handleConfirmRefund,
  loading,
}) => {
  const consents = useSelector(consentsSelector);
  const requiredConsents = useMemo(
    () =>
      consents?.items
        ?.filter(({ requiredForCustomerActions }) =>
          requiredForCustomerActions?.some(
            (action) => action.id === 'CUSTOMER_ACTION.TICKET_CANCELLATION'
          )
        )
        .map(({ id, description, name }) => ({
          id,
          text: description || name || '',
        })) || [],
    [consents?.items]
  );
  const [acceptedConsentsMap, setAcceptedConsentsMap] = useState<
    Record<number, boolean>
  >(requiredConsents.reduce((acc, c) => ({ ...acc, [c.id]: false }), {}));
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col justify-start gap-1">
        {requiredConsents.map((consent) => (
          <Checkbox
            key={consent.id}
            className="h-6 w-6"
            id={`consent-${consent.id}`}
            checked={acceptedConsentsMap[consent.id]}
            onCheckedChange={(checked) =>
              setAcceptedConsentsMap((prev) => ({
                ...prev,
                [consent.id]: checked === true,
              }))
            }
          >
            <Typography
              variant="body1"
              className="[&_a]:text-primary [&_a]:underline hover:[&_a]:text-primary-dark"
              dangerouslySetInnerHTML={{
                __html: sanitizeMarkup(consent.text),
              }}
            />
          </Checkbox>
        ))}
      </div>
      <div className="flex gap-2 laptop:justify-end">
        <Button
          variant="tertiary"
          data-testid="back-btn"
          onClick={handleBackToSelection}
          className="rounded-lg laptop:w-auto"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          fullWidth
        >
          <Icons.arrowLeft
            height={isLaptopOrBigger ? 16 : 24}
            width={isLaptopOrBigger ? 16 : 24}
          />
          <TransText i18nKey="back" />
        </Button>
        <Button
          data-testid="confirm-refund-btn"
          onClick={handleConfirmRefund}
          disabled={
            loading || !Object.values(acceptedConsentsMap).every(Boolean)
          }
          className="rounded-lg laptop:w-auto"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          fullWidth
        >
          <Icons.ticketReturn
            height={isLaptopOrBigger ? 16 : 24}
            width={isLaptopOrBigger ? 16 : 24}
          />
          <TransText i18nKey="confirm" />
        </Button>
      </div>
    </div>
  );
};

export default RefundModalOverviewFooter;
